/**
 * Tasks Service
 * API operations for task management
 */
import { apiClient } from './apiClient';
import { API_ENDPOINTS } from '@/constants/api';
import type {
  CreateTaskRequest,
  UpdateTaskRequest,
  TaskFiltersParams,
  MapTasksParams,
} from '@/types/api/requests';
import type {
  TaskResponse,
  TaskListResponse,
  TaskMapResponse,
  TaskMetadataResponse,
} from '@/types/api/responses';

export const tasksService = {
  /**
   * Create a new task
   */
  create: (data: CreateTaskRequest) =>
    apiClient.post<TaskResponse>(API_ENDPOINTS.TASKS.BASE, data),

  /**
   * Get all tasks with optional filters and pagination
   */
  getAll: (filters?: TaskFiltersParams) =>
    apiClient.get<TaskListResponse>(API_ENDPOINTS.TASKS.BASE, {
      params: filters,
    }),

  /**
   * Get tasks for map view within geographic bounds
   */
  getForMap: (params: MapTasksParams) =>
    apiClient.get<TaskMapResponse[]>(API_ENDPOINTS.TASKS.MAP, { params }),

  /**
   * Get task metadata for filters (assignees, roads, status/priority counts)
   */
  getMetadata: () =>
    apiClient.get<TaskMetadataResponse>(API_ENDPOINTS.TASKS.METADATA),

  /**
   * Get task by ID with all relations
   */
  getById: (id: string) =>
    apiClient.get<TaskResponse>(API_ENDPOINTS.TASKS.BY_ID(id)),

  /**
   * Get task by short ID (NanoID) with all relations
   */
  getByShortId: (shortId: string) =>
    apiClient.get<TaskResponse>(API_ENDPOINTS.TASKS.BY_SHORT_ID(shortId)),

  /**
   * Update task properties
   * Returns wrapped response with full task DTO
   */
  update: (id: string, data: UpdateTaskRequest) =>
    apiClient.put<{ task: TaskResponse }>(API_ENDPOINTS.TASKS.BY_ID(id), data),

  /**
   * Update task status only (partial update)
   * Returns the full task DTO with updated status
   */
  updateStatus: (id: string, status: string) =>
    apiClient.patch<{ task: TaskResponse }>(API_ENDPOINTS.TASKS.STATUS(id), {
      status,
    }),

  /**
   * Delete task by ID
   */
  delete: (id: string) => apiClient.delete<void>(API_ENDPOINTS.TASKS.BY_ID(id)),
};
